import {
  DestroyRef,
  ErrorHandler,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';

const RESIZE_OBSERVER_IGNORED_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

function shouldIgnoreResizeObserverError(event: ErrorEvent): boolean {
  const eventMessage = event.message ?? '';
  const errorMessage = event.error instanceof Error ? event.error.message : '';

  return RESIZE_OBSERVER_IGNORED_MESSAGES.some(
    (message) =>
      eventMessage.includes(message) || errorMessage.includes(message),
  );
}

export function provideBrowserGlobalErrorListenersWithFilter(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const errorHandler = inject(ErrorHandler);
      const destroyRef = inject(DestroyRef);

      const rejectionListener = (event: PromiseRejectionEvent) => {
        errorHandler.handleError(event.reason);
        event.preventDefault();
      };

      const errorListener = (event: ErrorEvent) => {
        if (shouldIgnoreResizeObserverError(event)) {
          event.preventDefault();
          return;
        }

        if (event.error) {
          errorHandler.handleError(event.error);
          event.preventDefault();
          return;
        }

        errorHandler.handleError(
          new Error(event.message || 'An ErrorEvent with no error occurred.', {
            cause: event,
          }),
        );
        event.preventDefault();
      };

      window.addEventListener('unhandledrejection', rejectionListener);
      window.addEventListener('error', errorListener);

      destroyRef.onDestroy(() => {
        window.removeEventListener('error', errorListener);
        window.removeEventListener('unhandledrejection', rejectionListener);
      });
    }),
  ]);
}
